/**
 * Wrapper de vídeo con aspect ratio 16:9. Elige entre dos formas
 * de reproducción según la URL:
 *
 *   - Ficheros directos (`.mp4`, `.webm`, `.mov`, `.m4v`) — típico
 *     cuando los subimos manualmente a S3 / CloudFront — se pintan
 *     con `<video controls>` HTML5 nativo. Streaming directo, sin
 *     necesidad de reproductor externo.
 *   - Cualquier otra URL (YouTube unlisted, Vimeo, S3 servido por
 *     un reproductor externo, etc.) se pinta con `<iframe>`.
 *
 * Si `embedUrl` es null → placeholder "Pendiente de subir" con CTA
 * de subida (visible sólo para admin).
 */

/** Detecta si la URL apunta a un fichero de vídeo servido directo
 *  (S3, CloudFront, etc.). Ignora query string y fragment. */
function isDirectVideoFile(url: string): boolean {
  const withoutQuery = url.split("?")[0].split("#")[0];
  return /\.(mp4|webm|mov|m4v|ogg)$/i.test(withoutQuery);
}

import { Button, Card, Typography } from "antd";
import {
  ClockCircleOutlined,
  CloudUploadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { useGetIdentity } from "@refinedev/core";
import type { VideoEntry } from "@/data/onboarding/types";
import type { UserInfo } from "@/types/auth";
import { ADMIN_USER_TYPES } from "@/types/auth";
import {
  resolveVideoUrl,
  useOnboardingAssets,
} from "./OnboardingAssetsContext";

const { Title, Text } = Typography;

interface Props {
  video: VideoEntry;
}

export const VideoEmbed: React.FC<Props> = ({ video }) => {
  const { assetsBySlug } = useOnboardingAssets();
  const effectiveUrl = resolveVideoUrl(
    video.slug,
    video.embedUrl,
    assetsBySlug,
  );
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  return (
  <Card size="small" style={{ height: "100%" }}>
    <Title level={5} style={{ marginTop: 0 }}>
      {video.title}
    </Title>
    {video.description ? (
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
        {video.description}
      </Text>
    ) : null}
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingTop: "56.25%",
        background: "#f0f0f0",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      {effectiveUrl ? (
        isDirectVideoFile(effectiveUrl) ? (
          <video
            src={effectiveUrl}
            controls
            preload="metadata"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "#000",
            }}
          >
            Tu navegador no soporta la reproducción de vídeo HTML5.
          </video>
        ) : (
          <iframe
            src={effectiveUrl}
            title={video.title}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            color: "#8c8c8c",
          }}
        >
          <VideoCameraOutlined style={{ fontSize: 36 }} />
          <Text type="secondary">
            <ClockCircleOutlined /> Pendiente de subir
          </Text>
          {isAdmin ? (
            <Link to="/onboarding/admin/assets">
              <Button
                size="small"
                icon={<CloudUploadOutlined />}
                type="dashed"
              >
                Añadir URL
              </Button>
            </Link>
          ) : null}
        </div>
      )}
    </div>
  </Card>
  );
};
